import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnDestroy
} from '@angular/core';
import {
    FormGroup,
    FormControl,
    Validators
} from '@angular/forms';
import { BehaviorSubject, Subject, Observable } from 'rxjs';
import { tap, map, combineLatest, takeUntil, switchMap, filter } from 'rxjs/operators';

import {
    DECORATION_PIXELIZATION_COST,
    DECORATION_RESTRICT_PIXELIZATION_COST
} from '../constants';

const MAX_COUNT = 24;

@Component({
    selector: 'app-decoration-pixelization-form',
    templateUrl: './decoration-pixelization-form.component.pug',
    styleUrls: ['./decoration-pixelization-form.component.less']
})
export class DecorationPixelizationFormComponent implements OnInit, OnDestroy {
    _destroySbj: Subject<void> = new Subject();

    public form: FormGroup = new FormGroup({
        count: new FormControl(1, Validators.min(1)),
        theme: new FormControl('')
    });

    _pixelsSbj: BehaviorSubject<number> = new BehaviorSubject(0);
    public pixels$: Observable<number> = this._pixelsSbj.asObservable();
    @Input() set pixels(pixels: number) {
        this._pixelsSbj.next(pixels || 0);
    }

    _themesSbj: BehaviorSubject<IDecorationsTheme[]> = new BehaviorSubject([]);
    public themes$: Observable<any[]> = this._themesSbj.asObservable()
        .pipe(map((themes) => themes.filter(({ restricted }) => !restricted)))
        .pipe(map((themes) => themes.map(({ _id: value, name }) => ({ value, name }))))
        .pipe(map((themes) => [{ value: '', name: 'All' }, ...themes]));
    @Input() set themes(themes: IDecorationsTheme[]) {
        this._themesSbj.next(themes);
    }
    public selectedThemeColor$: Observable<string> = this.form.valueChanges
        .pipe(map(({ theme }) => this._themesSbj.getValue().find(({ _id }) => _id === theme )))
        .pipe(map((theme) => theme ? theme.color : null));

    _costSbj: BehaviorSubject<number> = new BehaviorSubject(DECORATION_PIXELIZATION_COST);
    cost$: Observable<number> = this._costSbj.asObservable();

    max$ = this._pixelsSbj.asObservable()
        .pipe(combineLatest(this.cost$))
        .pipe(map(([pixels, cost]) => Math.floor(pixels / cost)))
        .pipe(map((max) => Math.min(max, MAX_COUNT)));

    @Output() pixelize: EventEmitter<any> = new EventEmitter();

    constructor(
    ) {
    }

    ngOnInit() {
        const theme$ = this.form.valueChanges
            .pipe(takeUntil(this._destroySbj))
            .pipe(tap(({ theme }) => this._costSbj.next(theme ? DECORATION_RESTRICT_PIXELIZATION_COST : DECORATION_PIXELIZATION_COST)));

        theme$.pipe(switchMap(() => this.max$))
            .pipe(filter((max) => max < this.form.value.count))
            .pipe(tap((count) => this.form.patchValue({ count })))
            .subscribe();

        theme$.pipe(switchMap(() => this.max$))
            .pipe(filter((max) => max > 0 && this.form.value.count === 0))
            .pipe(tap(() => this.form.patchValue({ count: 1 })))
            .subscribe();
    }

    ngOnDestroy() {
        this._destroySbj.next();
        this._destroySbj.complete();
    }

    onPixelize() {
        this.pixelize.emit(this.form.value);
    }
}
